import { UserAvatarResponse, AvatarStatus } from '@/types/avatar';
import { resolveApiOrigin, resolveAvatarUrlWithOrigin } from '@/utils/avatarUrl';
import { debug } from '@/utils/debug';

const getApiOrigin = () => {
  const configuredApiUrl = import.meta.env.VITE_API_URL;
  const windowOrigin = typeof window !== 'undefined' ? window.location.origin : null;
  const apiOrigin = resolveApiOrigin(configuredApiUrl, windowOrigin);

  if (configuredApiUrl && !apiOrigin) {
    debug.warn('Invalid VITE_API_URL when resolving avatar URL', configuredApiUrl);
  }

  return apiOrigin;
};

export const resolveAvatarUrl = (value: string | null | undefined) => {
  return resolveAvatarUrlWithOrigin(value, getApiOrigin());
};

export const normalizeAvatarStatus = (
  value: string | null | undefined
): AvatarStatus | 'REMOVED' => {
  if (!value) {
    return 'REMOVED';
  }

  const normalizedValue = value.toUpperCase();
  if (
    normalizedValue === 'PROCESSING' ||
    normalizedValue === 'READY' ||
    normalizedValue === 'REJECTED' ||
    normalizedValue === 'REMOVED'
  ) {
    return normalizedValue;
  }

  return 'REMOVED';
};

export const normalizeAvatarResponse = (avatar: UserAvatarResponse): UserAvatarResponse => ({
  ...avatar,
  status: normalizeAvatarStatus(avatar.status),
  avatar_url: resolveAvatarUrl(avatar.avatar_url),
  avatar_url_48: resolveAvatarUrl(avatar.avatar_url_48),
  avatar_url_128: resolveAvatarUrl(avatar.avatar_url_128),
  avatar_url_256: resolveAvatarUrl(avatar.avatar_url_256),
});

export const getAvatarDisplayUrl = (
  avatar: UserAvatarResponse | null | undefined,
  size: '48' | '128' | '256' = '256'
) => {
  if (!avatar) {
    return null;
  }

  const urlPriorityBySize = {
    '48': [avatar.avatar_url_48, avatar.avatar_url, avatar.avatar_url_256, avatar.avatar_url_128],
    '128': [avatar.avatar_url_128, avatar.avatar_url, avatar.avatar_url_256, avatar.avatar_url_48],
    '256': [avatar.avatar_url_256, avatar.avatar_url, avatar.avatar_url_128, avatar.avatar_url_48],
  };

  return urlPriorityBySize[size].find(Boolean) ?? null;
};

export const isAvatarTerminalStatus = (status?: string | null) => {
  const normalizedStatus = normalizeAvatarStatus(status);
  return (
    normalizedStatus === 'READY' ||
    normalizedStatus === 'REJECTED' ||
    normalizedStatus === 'REMOVED'
  );
};

export const getAvatarStatusLabel = (status?: string | null) => {
  const normalizedStatus = normalizeAvatarStatus(status);

  switch (normalizedStatus) {
    case 'PROCESSING':
      return 'Processing image...';
    case 'READY':
      return 'Photo ready';
    case 'REJECTED':
      return 'Photo rejected';
    case 'REMOVED':
    default:
      return 'No photo uploaded';
  }
};

export const getAvatarInitials = (name?: string | null) => {
  if (!name) {
    return 'U';
  }

  const initials = name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join('');

  return initials || 'U';
};
